/**
 * 批量建号页"自动生成用户信息"功能用的美式姓名生成器。
 *
 * 不依赖任何外部姓名库(避免引入新的 npm 依赖),用一份常见美国人名/姓
 * 的静态列表随机组合。姓名只是用来填充 given_name/family_name/username,
 * 不追求人口统计学意义上的真实分布,足够生成看起来自然、不重复的测试账号。
 */

const GIVEN_NAMES = [
  "James", "Mary", "Robert", "Patricia", "John", "Jennifer", "Michael", "Linda",
  "David", "Elizabeth", "William", "Barbara", "Richard", "Susan", "Joseph", "Jessica",
  "Thomas", "Sarah", "Charles", "Karen", "Christopher", "Nancy", "Daniel", "Lisa",
  "Matthew", "Betty", "Anthony", "Margaret", "Mark", "Sandra", "Donald", "Ashley",
  "Steven", "Kimberly", "Andrew", "Emily", "Paul", "Donna", "Joshua", "Michelle",
  "Kenneth", "Dorothy", "Kevin", "Carol", "Brian", "Amanda", "George", "Melissa",
  "Timothy", "Deborah", "Ronald", "Stephanie", "Jason", "Rebecca", "Edward", "Sharon",
  "Jeffrey", "Laura", "Ryan", "Cynthia", "Jacob", "Kathleen", "Gary", "Amy",
  "Nicholas", "Angela", "Eric", "Shirley", "Jonathan", "Anna", "Stephen", "Brenda",
  "Larry", "Pamela", "Justin", "Emma", "Scott", "Nicole", "Brandon", "Helen",
];

const FAMILY_NAMES = [
  "Smith", "Johnson", "Williams", "Brown", "Jones", "Garcia", "Miller", "Davis",
  "Rodriguez", "Martinez", "Hernandez", "Lopez", "Gonzalez", "Wilson", "Anderson", "Thomas",
  "Taylor", "Moore", "Jackson", "Martin", "Lee", "Perez", "Thompson", "White",
  "Harris", "Sanchez", "Clark", "Ramirez", "Lewis", "Robinson", "Walker", "Young",
  "Allen", "King", "Wright", "Scott", "Torres", "Nguyen", "Hill", "Flores",
  "Green", "Adams", "Nelson", "Baker", "Hall", "Rivera", "Campbell", "Mitchell",
  "Carter", "Roberts", "Gomez", "Phillips", "Evans", "Turner", "Diaz", "Parker",
  "Cruz", "Edwards", "Collins", "Reyes", "Stewart", "Morris", "Morales", "Murphy",
  "Cook", "Rogers", "Gutierrez", "Ortiz", "Morgan", "Cooper", "Peterson", "Bailey",
  "Reed", "Kelly", "Howard", "Ramos", "Kim", "Cox", "Ward", "Richardson",
];

export interface GeneratedUser {
  username: string;
  given_name: string;
  family_name: string;
  email: string;
}

function pickRandom<T>(items: T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

/**
 * 生成 count 个不重复的美式姓名用户(username/email 全局去重,依据
 * existingUsernames/existingEmails 传入的已存在集合)。
 *
 * username 格式: {given_name}.{family_name}(全小写),如 jennifer.swift,
 * 与项目里已有的真实测试账号命名风格一致(见部署过程中的 jennifer.swift 示例)。
 * 若基础用户名已被占用,追加数字后缀(jennifer.swift2、jennifer.swift3...)
 * 直到找到未占用的组合。
 */
export function generateUsers(
  count: number,
  emailDomain: string,
  existingUsernames: Set<string>
): GeneratedUser[] {
  const used = new Set(existingUsernames);
  const results: GeneratedUser[] = [];

  // 姓名组合空间是 90*90=8100,count 远小于这个数量级时随机碰撞概率很低,
  // 但仍加一个安全上限避免极端情况下(如 existingUsernames 覆盖了几乎所有
  // 常见组合)死循环。
  const maxAttempts = Math.max(count * 50, 1000);
  let attempts = 0;

  while (results.length < count && attempts < maxAttempts) {
    attempts += 1;
    const givenName = pickRandom(GIVEN_NAMES);
    const familyName = pickRandom(FAMILY_NAMES);
    const base = `${givenName}.${familyName}`.toLowerCase();

    let username = base;
    let suffix = 1;
    while (used.has(username)) {
      suffix += 1;
      username = `${base}${suffix}`;
    }
    used.add(username);

    results.push({
      username,
      given_name: givenName,
      family_name: familyName,
      email: `${username}@${emailDomain}`,
    });
  }

  return results;
}
